import { Shield, Crown, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * AdminBadge - Prominent admin flair component
 * Use site-wide to identify admin users
 */
export const AdminBadge = ({ 
  size = 'default', // 'small', 'default', 'large'
  showText = true,
  animated = true,
  className = ''
}) => {
  const sizes = {
    small: {
      container: 'px-1.5 py-0.5 text-[10px] gap-1',
      icon: 10
    },
    default: {
      container: 'px-2 py-1 text-xs gap-1.5',
      icon: 12
    },
    large: {
      container: 'px-3 py-1.5 text-sm gap-2',
      icon: 16
    }
  };

  const sizeConfig = sizes[size] || sizes.default;

  const Wrapper = animated ? motion.div : 'div';
  const wrapperProps = animated ? {
    initial: { scale: 0.9, opacity: 0.8 },
    animate: { scale: 1, opacity: 1 },
    whileHover: { scale: 1.05 },
    transition: { duration: 0.2 }
  } : {};

  return (
    <Wrapper
      {...wrapperProps}
      className={`
        inline-flex items-center ${sizeConfig.container}
        bg-gradient-to-r from-yellow-500/20 via-amber-500/30 to-yellow-500/20
        border border-yellow-500/50
        rounded-full
        font-bold tracking-wide uppercase
        text-yellow-400
        shadow-lg shadow-yellow-500/20
        ${className}
      `}
    >
      <Crown size={sizeConfig.icon} className="text-yellow-400" />
      {showText && <span>Admin</span>}
    </Wrapper>
  );
};

/**
 * AdminFlairBanner - Full admin banner for profiles/headers
 */
export const AdminFlairBanner = ({ username = 'Admin', className = '' }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`
        flex items-center justify-center gap-3 py-2 px-4
        bg-gradient-to-r from-yellow-600/20 via-amber-500/30 to-yellow-600/20
        border-y border-yellow-500/30
        ${className}
      `}
    >
      <Sparkles size={14} className="text-yellow-400 animate-pulse" />
      <span className="text-yellow-400 font-bold text-sm tracking-wider uppercase">
        Site Administrator
      </span>
      <Sparkles size={14} className="text-yellow-400 animate-pulse" />
    </motion.div>
  );
};

/**
 * AdminNameDisplay - Admin username with integrated flair
 */
export const AdminNameDisplay = ({ 
  name, 
  size = 'default',
  className = '' 
}) => {
  const sizes = {
    small: 'text-sm',
    default: 'text-base',
    large: 'text-lg'
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span className={`font-bold text-yellow-400 ${sizes[size]}`}>
        {name}
      </span>
      <AdminBadge size={size === 'large' ? 'default' : 'small'} showText={size !== 'small'} />
    </div>
  );
};

/**
 * AdminAvatarRing - Golden animated ring for admin avatars
 */
export const AdminAvatarRing = ({ children, size = 48, className = '' }) => {
  return (
    <div 
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{ width: size + 8, height: size + 8 }}
    >
      {/* Animated glow ring */}
      <motion.div
        animate={{ 
          boxShadow: [
            '0 0 10px rgba(234, 179, 8, 0.3), 0 0 20px rgba(234, 179, 8, 0.2)',
            '0 0 15px rgba(234, 179, 8, 0.5), 0 0 30px rgba(234, 179, 8, 0.3)',
            '0 0 10px rgba(234, 179, 8, 0.3), 0 0 20px rgba(234, 179, 8, 0.2)',
          ]
        }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute inset-0 rounded-full border-2 border-yellow-500"
      />
      {/* Inner content */}
      <div 
        className="relative rounded-full overflow-hidden border-2 border-yellow-400"
        style={{ width: size, height: size }}
      >
        {children}
      </div>
      {/* Crown icon at top */}
      <div className="absolute -top-1 left-1/2 -translate-x-1/2 bg-babylon-void rounded-full p-0.5">
        <Crown size={12} className="text-yellow-400" />
      </div>
    </div>
  );
};

export default AdminBadge;
